import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import {
  RelatedArticlesBand,
  RelatedArticlesSidebar,
  type RelatedArticle,
} from "@/components/gisela/RelatedArticles"
import { bandBg } from "@/components/gisela/TextBand"
import { useLocalePath } from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import { orderBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

type Photo = HeroPhoto

interface Subtype {
  label: string
  path: string
  blurb: string
}

interface Section {
  heading?: string
  body?: string
  _hidden?: boolean
}

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: Photo | null; _hidden?: boolean }
    what_is: Section
    hidden: Section
    approach: { body?: string; photo?: Photo }
    previous_therapy: Section
    why_specialist: Section
    course: { body?: string }
    faq: { body?: string }
    // The closing pair every top-level condition page ends on.
    cta?: { photo?: Photo | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  subtypes?: Subtype[]
  articles?: RelatedArticle[]
  /** The home page's tile slot — one card per page of her site. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** A room of hers, standing beside the short enquiry form. */
  contact_photo?: PlacementPhoto | null
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

// The OCD pillar page — the flagship of the OCD-first positioning
// (docs/two-brand-split-strategy.md §13.1). All copy is CMS-driven; the
// subtype grid links the cluster pages under /ocd-therapy/ (GiselaConditions).
// Band order comes from section_order (PageContentRegistry + the admin's
// PageSectionOrder), so each band names its registry entry id.
export default function OcdTherapy({
  content,
  subtypes,
  articles,
  tile_photos = {},
  contact_photo,
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.ocd
  const tc = props.translations.gisela_pages.common
  const lp = useLocalePath()
  const title = meta?.title ?? "OCD Therapy | CBT & ERP Online Worldwide | Gisela Ramirez"
  const description =
    meta?.description ??
    "Specialist OCD therapy with Gisela Ramirez — BABCP-accredited CBT and ERP, online across the UK and worldwide, in person in Manchester. English and Spanish."

  // Every band below the hero is presence-gated — blank one in the admin and a
  // hardcoded cream/stone alternation would leave two same-coloured bands
  // touching. So build the list of bands that actually have content, put them
  // in the admin's order, then colour by index (as gisela/Fees.tsx does).
  const bands = orderBands(
    [
      {
        id: "ocd-types",
        heading: t.types_heading,
        body: subtypes && subtypes.length > 0 ? t.types_blurb : undefined,
        grid: true,
      },
      {
        id: "ocd-what-is",
        heading: content.what_is.heading || t.what_is_heading,
        body: content.what_is.body,
      },
      { id: "ocd-hidden", heading: content.hidden.heading || t.hidden_heading, body: content.hidden.body },
      {
        id: "ocd-approach",
        heading: t.approach_heading,
        body: content.approach.body,
        photo: content.approach.photo,
      },
      {
        id: "ocd-previous-therapy",
        heading: content.previous_therapy.heading || t.previous_therapy_heading,
        body: content.previous_therapy.body,
      },
      {
        id: "ocd-why-specialist",
        heading: content.why_specialist.heading || t.why_specialist_heading,
        body: content.why_specialist.body,
      },
      { id: "ocd-course", heading: t.course_heading, body: content.course.body },
      { id: "ocd-faq", heading: tc.common_questions, body: content.faq.body },
    ] as { id: string; heading: string; body?: string; grid?: boolean; photo?: Photo }[],
    section_order,
  ).filter((band) => band.body)

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* Hero — short, no buttons, so the subtype grid is in view straight away. */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
              {t.eyebrow}
            </p>
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {/* Everything below the hero reserves the right gutter (xl+) for the
            related-articles sidebar; the bands' full-bleed backgrounds still
            span the viewport because the padding sits on each section. */}
        {/* The gutter keeps a column's worth of height when the sidebar is
            in it: a page whose bands are shorter than its reading list would
            otherwise leave the sticky column nowhere to sit, and it would
            spill over the bands the page closes with. */}
        <div
          className={cn(
            "relative xl:[&>section]:pr-80",
            !!articles?.length && "xl:min-h-[34rem]",
          )}
        >
          {bands.map((band, i) =>
            band.grid ? (
              /* Types of OCD — the subtype cluster. A card grid rather than the
                 sticky-heading body layout the other bands use. */
              <section key={band.id} className={`${bandBg(i)} py-16`}>
                <div className="mx-auto max-w-6xl px-6 lg:px-8">
                  <h2>{band.heading}</h2>
                  <p className="mt-3 text-ink-muted">{band.body}</p>
                  <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {subtypes?.map((s) => (
                      <div key={s.path} className="rounded-2xl bg-white p-6 ring-1 ring-hairline">
                        <h3>
                          <a href={lp(s.path)}>{s.label}</a>
                        </h3>
                        <p className="mt-2 text-ink-muted">{s.blurb}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ) : (
              <section key={band.id} className={`${bandBg(i)} py-16`}>
                <div className="mx-auto grid max-w-6xl grid-cols-1 px-6 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-x-16 lg:px-8">
                  <h2 className="lg:sticky lg:top-24 lg:self-start">{band.heading}</h2>
                  <div className="min-w-0">
                    {band.photo?.url && (
                      <img
                        src={band.photo.url}
                        alt={band.photo.alt_text ?? ""}
                        className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover"
                      />
                    )}
                    <div className="body-content mt-6 text-ink-body">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{band.body}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </section>
            ),
          )}

          {/* Articles for the pillar and every OCD subtype — a band below xl,
              a sidebar above; continues the alternation. */}
          <RelatedArticlesBand articles={articles} className={bandBg(bands.length)} />

          <RelatedArticlesSidebar articles={articles} />
        </div>

        {/* The close every top-level condition page shares: the invitation —
            one ask, the free consultation — then the tiles out of it, then a
            room beside the short form. These sit outside the sidebar's
            gutter: they are full-width bands, and the reading column they
            would otherwise be squeezed into has finished by here. */}
        {!sectionHidden(content.cta) && (
          <ContactBand
            heading={t.cta_heading}
            body={t.cta_body}
            photo={content.cta?.photo}
            href="/book"
            label={t.cta_button}
          />
        )}

        <GiselaPageTiles photos={tile_photos} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
            voice="hers"
          />
        )}
      </PublicLayout>
    </>
  )
}
